// Quake 3 style skybox ("skyparms env/name"): six images *_rt/_bk/_lf/_ft/_up/_dn
// mapped exactly like tr_sky.c, drawn around the camera before the world. Only used
// for maps without sky surfaces; sky surfaces use q3shader.createSkyMaterial.

import * as THREE from 'three';
import type { TextureManager } from './textures';

const SUFFIXES = ['rt', 'bk', 'lf', 'ft', 'up', 'dn'];
// tr_sky.c st_to_vec: maps (s, t, dist) to a world direction
const ST_TO_VEC = [
  [3, -1, 2],
  [-3, 1, 2],
  [1, 3, 2],
  [-1, -3, 2],
  [-2, -1, 3],
  [2, -1, -3],
];

function skyVec(axis: number, s: number, t: number, dist: number): THREE.Vector3 {
  const b = [s * dist, t * dist, dist];
  const v = [0, 0, 0];
  for (let j = 0; j < 3; j++) {
    const k = ST_TO_VEC[axis][j];
    v[j] = k < 0 ? -b[-k - 1] : b[k - 1];
  }
  return new THREE.Vector3(v[0], v[1], v[2]);
}

export class SkyBox {
  readonly object = new THREE.Group();
  private materials: THREE.Material[] = [];
  private geometries: THREE.BufferGeometry[] = [];

  static async create(name: string | null, textures: TextureManager): Promise<SkyBox> {
    const sky = new SkyBox();
    const images = name ? await Promise.all(SUFFIXES.map((s) => textures.load(`${name}_${s}`, true))) : [];
    const ok = images.length === 6 && images.every(Boolean);
    const dist = 10;
    for (let axis = 0; axis < 6; axis++) {
      const corners = [
        skyVec(axis, -1, -1, dist),
        skyVec(axis, 1, -1, dist),
        skyVec(axis, 1, 1, dist),
        skyVec(axis, -1, 1, dist),
      ];
      const geo = new THREE.BufferGeometry().setFromPoints(corners);
      // s,t in [-1,1] -> texture (s+1)/2, 1-(t+1)/2 (t=+1 is the top row)
      geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 1, 1, 1, 1, 0, 0, 0], 2));
      geo.setIndex([0, 1, 2, 0, 2, 3]);
      const mat = ok
        ? new THREE.MeshBasicMaterial({ map: images[axis]!, side: THREE.DoubleSide, depthWrite: false, depthTest: false })
        : new THREE.MeshBasicMaterial({ color: axis === 5 ? 0x2a2f38 : axis === 4 ? 0x7fa6d6 : 0x5d7fa8, side: THREE.DoubleSide, depthWrite: false, depthTest: false });
      if (ok) images[axis]!.wrapS = images[axis]!.wrapT = THREE.ClampToEdgeWrapping;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.renderOrder = -1000;
      mesh.frustumCulled = false;
      sky.object.add(mesh);
      sky.materials.push(mat);
      sky.geometries.push(geo);
    }
    return sky;
  }

  follow(camera: THREE.Camera): void {
    this.object.position.copy(camera.position);
  }

  dispose(): void {
    this.materials.forEach((m) => m.dispose());
    this.geometries.forEach((g) => g.dispose());
  }
}
